/* eslint-disable react-hooks/exhaustive-deps */
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { LoadingButton } from "@mui/lab";
import {
  Badge,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { Box, styled } from "@mui/system";
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridRowIdGetter,
} from "@mui/x-data-grid";
import {
  AppNoTableData,
  AppSearch,
  FileSection,
  GridCard,
} from "components/atoms";
import { DialogHelper, RoleHelper, TableHelper } from "helpers";
import { useWindowsResize } from "hooks/useWindowsResize";
import { isUndefined, sum } from "lodash";
import { IPaginateApiResponse } from "model/Common/IPaginateApiResponse";
import React, { createRef, Fragment, useEffect, useState } from "react";
import { AppConfigActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { theme } from "themes";
import { CURRENT_PAGE_SIZE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
const PAGE_SIZES = [25, 50, 70, 100];

const _fileId = "contained-button-file";

const DELAY_SEARCH_MILLISECOND = 300;

const NAVIGATION_ICON = 30;

const DEFAULT_ROW_HEIGHT = 34;

const TableContainer = styled(Box)(() => ({
  width: "100%",
}));

const HeaderContainer = styled(Grid)(() => ({
  width: "100%",
  marginTop: 0,
  padding: "16px 16px 0 16px",
}));

const HeaderContentContainer = styled(Grid)(() => ({
  width: "100%",
}));

const ToolbarFooterContainer = styled(Grid)(() => ({
  width: "100%",
}));

const ToolbarRightContentContainer = styled(Stack)(() => ({
  width: "100%",
}));

const PageCalculationTypography = styled(Typography)(() => ({
  marginRight: "20px",
}));

const PageCountTypography = styled(Typography)(() => ({
  padding: "0 10px",
}));

const CustomDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-columnHeaderTitle": {
    textOverflow: "clip",
    whiteSpace: "break-spaces",
    lineHeight: 1.5,
  },
}));

type Files = FileList | null;

type Params = GridCellParams<any, any, any>;

const getAdminRole =
  RoleHelper.isSuperAdmin() ||
  RoleHelper.isSupervisor() ||
  RoleHelper.isAdmin();

interface IToolbarProps {
  title?: string;
  hideHeader?: boolean;
  onAddNewClick?(): void;
  headerButtonsRight?: React.ReactNode;
  headerButtonsLeft?: React.ReactNode;
  bodyToolbarButton?: React.ReactNode;
  hideAddNew?: boolean;
  hideImport?: boolean;
  hideDownload?: boolean;
  onChangeText: (searchValue: string) => void;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  onChangePage(pageNumber: number): void;
  toolbarFooter?: JSX.Element;
  hideSearch?: boolean;
  hideFilter?: boolean;
  searchPlaceholder?: string;
  onPressFilter?(event: any): void;
  filterBadgeNumber?: number;
  onPageSizeChange?(size: number): void;
  isDownloading?: boolean;
  onDownloadClick?(): void;
}

interface IHeaderTableProps {
  title?: string;
  hideHeader?: boolean;
  onAddNewClick?(): void;
  onImportClick?(): void;
  onDownloadClick?(): void;
  headerButtonsLeft?: React.ReactNode;
  headerButtonsRight?: React.ReactNode;
  hideAddNew?: boolean;
  hideImport?: boolean;
  hideDownload?: boolean;
  isDownloading?: boolean;
}
interface IAppTableProps<T> {
  data?: Partial<IPaginateApiResponse<T[]>>;
  columns: GridColDef[];
  title?: string;
  loading?: boolean;
  headerFooter?: JSX.Element;
  removeActionColumn?: boolean;
  getRowId?: GridRowIdGetter;
  hideAddNew?: boolean;
  hideHeader?: boolean;
  hideSearch?: boolean;
  hideFilter?: boolean;
  hideImport?: boolean;
  hideDownload?: boolean;
  searchPlaceholder?: string;
  headerButtonsLeft?: React.ReactNode;
  headerButtonsRight?: React.ReactNode;
  bodyToolbarButton?: React.ReactNode;
  toolbarFooter?: React.ReactNode;
  headerProps?: {};
  actionColumnProps?: {
    hideImport?: boolean;
    hideEdit?: boolean;
    hideDelete?: boolean;
  };
  defaultHeaderTable?: number;
  filterBadgeNumber?: number;
  isDownloading?: boolean;
  onCellClick?(params: Params): void;
  onCellDoubleClick?(params: Params): void;
  onAddNewClick?(): void;
  onImportClick?(): void;
  onDownloadClick?(): void;
  onSearch(searchValue: string): void;
  onChangePage(pageNumber: number): void;
  onRowPressEdit?(row: T): void;
  onRowPressDelete?(row: T): void;
  onRowPressImport?(row: T): void;
  onPressFilter?(event: any): void;
  onFileSelected?(files: Files): void;
  onHeaderHeightChanged?(height: number): void;
  onPageSizeChange?(size: number): void;
  getRowClassName?(row: any): string;
}

const AppTable = <T extends { [key: string]: any }>(
  props: IAppTableProps<T>
) => {
  const {
    loading = false,
    data,
    columns,
    onSearch,
    onCellClick,
    onCellDoubleClick,
    onRowPressEdit,
    onRowPressDelete,
    removeActionColumn,
    actionColumnProps,
    getRowId = (row) => row.no,
    onRowPressImport,
    onFileSelected,
    onHeaderHeightChanged,
    filterBadgeNumber,
    onPageSizeChange,
    getRowClassName,
  } = props;

  let searchWaiting: ReturnType<typeof setTimeout>;

  const fRef = React.useRef<any>();
  const headerRef = createRef<any>();
  const windows = useWindowsResize();

  const dispatch = useAppDispatch();
  const action = useAppSelector(
    (state) => state.AppConfigReducer.currentAction
  );

  const [toolbarHeight, setToolbarHeight] = useState(0);

  const renderData = () => {
    /**
     * @Data will be [] when loading
     * @Add "no" field for dynamic data
     */
    const isNotLoading = !loading;
    const newData = !isUndefined(data)
      ? ((data.data as T[]) || []).map((item, index) => {
          return {
            no: index + 1,
            ...item,
          };
        })
      : [];
    return isNotLoading ? newData : [];
  };

  const renderColumns = () => {
    const arr = Array.from(columns);
    const CURRENT_ICON_WIDTH = 64;
    const _importWidth = actionColumnProps?.hideImport ? 0 : CURRENT_ICON_WIDTH;
    const _editWidth = actionColumnProps?.hideEdit ? 0 : CURRENT_ICON_WIDTH;
    const _deleteWidth = actionColumnProps?.hideDelete ? 0 : CURRENT_ICON_WIDTH;
    const actionWidth = sum([_importWidth, _editWidth, _deleteWidth]);
    const notRemoveActionColumn = !removeActionColumn;
    if (notRemoveActionColumn) {
      const actionRow = TableHelper.createCell({
        field: "action",
        headerName: I18nTranslator(translations.masterData.action),
        align: "center",
        headerAlign: "center",
        size: actionWidth,
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }: any) => (
          <div>
            {!actionColumnProps?.hideImport && (
              <label
                htmlFor={_fileId}
                onClick={() => handleRowPressImport(row)}
              >
                <IconButton
                  component="span"
                  sx={{
                    display: !getAdminRole ? "none" : "inline",
                  }}
                  disabled={!getAdminRole}
                >
                  <UploadFileIcon fontSize="small" />
                </IconButton>
              </label>
            )}
            {!actionColumnProps?.hideEdit && (
              <IconButton onClick={() => onRowPressEdit!(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {!actionColumnProps?.hideDelete && (
              <IconButton
                onClick={() => onRowPressDelete!(row)}
                sx={{
                  display: !(
                    RoleHelper.isSuperAdmin() || RoleHelper.isSupervisor()
                  )
                    ? "none"
                    : "inline",
                }}
                disabled={
                  !(RoleHelper.isSuperAdmin() || RoleHelper.isSupervisor())
                }
              >
                <DeleteIcon
                  sx={{ color: theme.colors.error.light }}
                  fontSize="small"
                />
              </IconButton>
            )}
          </div>
        ),
      });
      arr.push(actionRow);
      // arr.splice(1, 0, actionRow);
    }
    return arr;
  };

  const handleRowPressImport = (row: T) => {
    onRowPressImport!(row);
    clearFilePath();
  };

  const handleInputSearch = (searchValue: string) => {
    if (searchWaiting) {
      clearTimeout(searchWaiting);
    }
    searchWaiting = setTimeout(() => {
      onSearch(searchValue);
    }, DELAY_SEARCH_MILLISECOND);
  };

  const clearFilePath = () => {
    if (fRef.current) {
      fRef.current.value = "";
    }
  };
  const clearAction = () => {
    dispatch(AppConfigActions.clearAction.request());
  };
  const _willComponentUnmount = () => {
    /**
     * @Clear file path when unmount
     */
    clearFilePath();
    clearAction();
  };

  useEffect(() => {
    if (action.includes("IMPORT") && action.includes("FAILED")) {
      clearFilePath();
      DialogHelper.showImportErrorDialog();
    }
  }, [action]);

  useEffect(() => {
    if (headerRef) {
      const headerHeight = headerRef?.current?.offsetHeight;
      setToolbarHeight(headerHeight);
      onHeaderHeightChanged && onHeaderHeightChanged(headerHeight as number);
    }
  }, [windows, headerRef]);

  useEffect(() => {
    return () => {
      _willComponentUnmount();
    };
  }, []);

  return (
    <GridCard
      sx={{
        display: "flex",
        flex: 1,
        flexGrow: 1,
        width: "100%",
        height: "100%",
      }}
    >
      <div
        style={{
          flexDirection: "column",
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <FileSection ref={fRef} id={_fileId} onFileSelected={onFileSelected} />
        <div
          style={{
            flexGrow: 1,
            width: "100%",
          }}
        >
          <CustomDataGrid
            loading={loading}
            rows={renderData()}
            columns={renderColumns()}
            rowsPerPageOptions={[5]}
            disableColumnMenu
            onCellDoubleClick={onCellDoubleClick}
            onCellClick={onCellClick}
            hideFooter
            hideFooterSelectedRowCount
            hideFooterPagination
            getRowId={getRowId}
            rowHeight={DEFAULT_ROW_HEIGHT}
            getRowClassName={getRowClassName}
            pageSize={data?.pageSize || 0}
            components={{
              Toolbar: TableHeader,
              NoRowsOverlay: () => <AppNoTableData />,
            }}
            componentsProps={{
              toolbar: {
                ...props,
                ref: headerRef,
                onChangeText: handleInputSearch,
                currentPage: data?.pageNumber || 0,
                totalPages: data?.totalPages || 0,
                pageSize: data?.pageSize || 0,
                totalCount: data?.totalRecords || 0,
                filterBadgeNumber: filterBadgeNumber || 0,
                onPageSizeChange: onPageSizeChange,
              },
            }}
            style={{
              border: 0,
              minHeight: 600,
            }}
          />
        </div>
      </div>
    </GridCard>
  );
};

const TableHeader = React.forwardRef((props: IToolbarProps, ref: any) => {
  const {
    onChangeText,
    currentPage,
    totalPages,
    pageSize,
    totalCount,
    onChangePage,
    toolbarFooter,
    hideSearch,
    hideFilter = true,
    searchPlaceholder,
    onPressFilter,
    title,
    headerButtonsLeft,
    headerButtonsRight,
    hideAddNew,
    hideDownload,
    hideHeader,
    hideImport,
    onAddNewClick,
    bodyToolbarButton,
    filterBadgeNumber,
    onPageSizeChange,
    isDownloading,
    onDownloadClick,
  } = props;

  const _renderFooter = () => {
    return (
      <ToolbarFooterContainer item container sm={12} alignItems="center">
        {toolbarFooter}
      </ToolbarFooterContainer>
    );
  };

  return (
    <Box ref={ref}>
      <HeaderContainer
        item
        container
        sm={12}
        alignItems="center"
        rowSpacing={2}
      >
        <Header
          title={title}
          headerButtonsLeft={headerButtonsLeft}
          headerButtonsRight={headerButtonsRight}
          onAddNewClick={onAddNewClick}
          onDownloadClick={onDownloadClick}
          hideHeader={hideHeader}
          hideAddNew={hideAddNew}
          hideImport={hideImport}
          hideDownload={hideDownload}
          isDownloading={isDownloading}
        />
        <Body
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          hideFilter={hideFilter}
          hideSearch={hideSearch}
          searchPlaceholder={searchPlaceholder}
          onChangePage={onChangePage}
          onChangeText={onChangeText}
          onPressFilter={onPressFilter}
          onPageSizeChange={onPageSizeChange}
          bodyToolbarButton={bodyToolbarButton}
          filterBadgeNumber={filterBadgeNumber}
        />
        {_renderFooter()}
      </HeaderContainer>
    </Box>
  );
});

const Header = (props: IHeaderTableProps) => {
  const {
    title,
    onAddNewClick,
    onDownloadClick,
    onImportClick,
    headerButtonsLeft,
    headerButtonsRight,
    hideAddNew,
    hideDownload,
    hideImport,
    hideHeader,
    isDownloading,
  } = props;

  const _renderLeftContent = () => {
    return (
      <HeaderContentContainer item container sm={3} alignItems="center">
        <Typography
          variant="h2"
          style={{
            color: "black",
          }}
        >
          {title}
        </Typography>
      </HeaderContentContainer>
    );
  };

  const _renderRightContent = () => {
    return (
      <HeaderContentContainer
        item
        container
        sm={9}
        alignItems="center"
        justifyContent={"right"}
      >
        {headerButtonsLeft}
        {!hideImport && (
          <label htmlFor={_fileId}>
            <Button
              variant="contained"
              component="span"
              onClick={onImportClick}
              startIcon={<UploadFileIcon />}
            >
              {I18nTranslator(translations.button.importDocs)}
            </Button>
          </label>
        )}
        {!hideDownload && (
          <LoadingButton
            variant="contained"
            sx={{ marginLeft: "10px" }}
            onClick={onDownloadClick}
            loading={isDownloading}
            startIcon={<DownloadIcon />}
          >
            {I18nTranslator(translations.button.exportDocs)}
          </LoadingButton>
        )}
        {!hideAddNew && (
          <Button
            variant="contained"
            sx={{ marginLeft: "10px" }}
            onClick={onAddNewClick}
            startIcon={<AddIcon />}
          >
            {I18nTranslator(translations.button.addNew)}
          </Button>
        )}
        {headerButtonsRight}
      </HeaderContentContainer>
    );
  };

  if (hideHeader) {
    return null;
  }
  return (
    <Grid container justifyContent="space-between" alignItems="center">
      {_renderLeftContent()}
      {_renderRightContent()}
    </Grid>
  );
};

interface IBodyTableProps {
  onChangeText: (searchValue: string) => void;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  onChangePage(pageNumber: number): void;
  hideSearch?: boolean;
  hideFilter?: boolean;
  searchPlaceholder?: string;
  onPressFilter?(event: any): void;
  bodyToolbarButton: React.ReactNode;
  filterBadgeNumber?: number;
  onPageSizeChange?(size: number): void;
}

const Body = (props: IBodyTableProps) => {
  const {
    currentPage,
    onChangePage,
    onChangeText,
    pageSize,
    totalCount,
    totalPages,
    hideFilter,
    hideSearch,
    onPressFilter,
    searchPlaceholder,
    bodyToolbarButton,
    filterBadgeNumber,
    onPageSizeChange,
  } = props;

  const [rowOfPage, setRowsOfPage] = useState<number>(CURRENT_PAGE_SIZE);
  const disablePrev = currentPage <= 1;
  const disableNext = currentPage * pageSize >= totalCount;

  const getNavigationColor = (isDisable: boolean) => {
    return !isDisable ? "black" : "lightgrey";
  };

  const handleChangeRowsOfPage = (event: SelectChangeEvent<number>) => {
    const size = event.target.value as number;
    onPageSizeChange && onPageSizeChange(size);
    setRowsOfPage(size);
  };
  const _renderPageCalculation = () => {
    const pageRange = `${(currentPage - 1) * pageSize + 1}-${
      (currentPage - 1) * pageSize + pageSize >= totalCount
        ? totalCount
        : (currentPage - 1) * pageSize + pageSize
    }`;
    const pageCalculator = `${pageRange} ${I18nTranslator(
      translations.of
    )} ${totalCount}`;
    return (
      <PageCalculationTypography variant={"caption"}>
        {pageCalculator}
      </PageCalculationTypography>
    );
  };

  const _renderLeftNavigation = () => {
    return (
      <>
        <IconButton
          disabled={disablePrev}
          onClick={() => onChangePage(1)}
          size="small"
        >
          <FirstPageIcon
            style={{
              height: NAVIGATION_ICON,
              color: getNavigationColor(disablePrev),
            }}
          />
        </IconButton>
        <IconButton
          disabled={disablePrev}
          onClick={() => onChangePage(currentPage - 1)}
          size="small"
        >
          <NavigateBeforeIcon
            style={{
              height: NAVIGATION_ICON,
              color: getNavigationColor(disablePrev),
            }}
          />
        </IconButton>
      </>
    );
  };

  const _renderRightNavigation = () => {
    return (
      <>
        <IconButton
          disabled={disableNext}
          onClick={() => onChangePage(currentPage + 1)}
          size="medium"
        >
          <NavigateNextIcon
            style={{
              height: NAVIGATION_ICON,
              color: getNavigationColor(disableNext),
            }}
          />
        </IconButton>
        <IconButton
          disabled={disableNext}
          onClick={() => onChangePage(totalPages)}
          size="small"
        >
          <LastPageIcon
            style={{
              height: NAVIGATION_ICON,
              color: getNavigationColor(disableNext),
            }}
          />
        </IconButton>
      </>
    );
  };

  const _renderLeftContent = () => {
    return (
      <HeaderContentContainer
        item
        container
        sm={6}
        alignItems={"center"}
        justifyContent="right"
      >
        <ToolbarRightContentContainer
          direction={"row"}
          spacing={2}
          sx={{ height: hideSearch ? 36.5 : 40 }}
        >
          {!hideSearch && (
            <Grid item sx={{ flex: 1, height: 40 }}>
              <AppSearch
                placeholder={searchPlaceholder}
                onChangeText={onChangeText}
              />
            </Grid>
          )}
          {!hideFilter && (
            <Grid sx={{ height: 40 }} item>
              <Badge badgeContent={filterBadgeNumber} color="primary">
                <Button
                  id={"filter"}
                  sx={{ height: 40 }}
                  variant="contained"
                  startIcon={<FilterAltIcon />}
                  onClick={onPressFilter}
                >
                  {I18nTranslator(translations.table.filter)}
                </Button>
              </Badge>
            </Grid>
          )}
          {bodyToolbarButton}
        </ToolbarRightContentContainer>
      </HeaderContentContainer>
    );
  };
  const _renderRightContent = () => {
    return (
      <HeaderContentContainer
        item
        container
        sm={6}
        alignItems={"center"}
        justifyContent="right"
      >
        <PageCalculationTypography>
          <FormControl sx={{ width: 100 }}>
            <InputLabel id="demo-simple-select-label">
              {I18nTranslator(translations.table.rowNo)}
            </InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={rowOfPage}
              label="RowsOfPage"
              onChange={handleChangeRowsOfPage}
              sx={{
                height: "40px",
              }}
            >
              {PAGE_SIZES.map((item) => {
                return <MenuItem value={item}>{item}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </PageCalculationTypography>

        {_renderPageCalculation()}
        <Fragment>
          {_renderLeftNavigation()}
          <PageCountTypography
            variant={"caption"}
          >{`${currentPage}/${totalPages}`}</PageCountTypography>
          {_renderRightNavigation()}
        </Fragment>
      </HeaderContentContainer>
    );
  };

  return (
    <Grid sx={{ paddingTop: "8px" }} container sm={12}>
      {_renderLeftContent()}
      {_renderRightContent()}
    </Grid>
  );
};

export default AppTable;
